import "./auth.css";
import * as Yup from "yup";
import {
  login,
  setRetryAfter,
  setRedirectedToReset,
  clearRetryState,
} from "../../redux/slices/authSlice";
import {
  trackSignupClick,
  trackLoginFormSubmit,
  trackLoginFormExit,
  trackLoginWithGoogle,
  trackLoginWithFacebook,
  trackLoginSuccess,
} from "../../utils/GoogleTagTracking";
import { Formik, Form } from "formik";
import { routes } from "../../routes";
import { Modal } from "react-bootstrap";
import ForgetPassword from "./ForgetPassword";
import Toaster from "../../components/Toast/Toast";
import { useEffect, useState, useRef } from "react";
import Loading from "../../components/Loader/Loading";
import { authImages } from "../../utils/globalImages";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toggleLoader } from "../../redux/slices/loaderSlice";
import { toggleReFetch } from "../../redux/slices/themeSlice";
import { clearMessage } from "../../redux/slices/messageSlice";
import { togglePayment } from "../../redux/slices/accountSlice";
import WebLogoDark from "../../assets/images/logo/login-logo.png";
import { toggleOnboard } from "../../redux/slices/onBoardingSlice";
import BackdropLoader from "../../components/BackdropLoader/BackdropLoader";

const Login = () => {
  const startY = useRef(0);
  const cardRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isSubmittingRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const onboarding = useSelector((state) => state?.onboarding);
  const retryAfter = useSelector((state) => state.auth.retryAfter);
  const isDarkMode = useSelector((state) => state?.theme?.darkMode);
  const [message, setMessage] = useState({ success: "", error: "" });
  const isLoading = useSelector((state) => state?.loader?.isLoading);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 641);
  const hasRedirectedToReset = useSelector(
    (state) => state.auth.hasRedirectedToReset,
  );

  // Swipe to close states
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 641);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  useEffect(() => {
    if (onboarding?.login) {
      setDragY(0);
      setIsDragging(false);
    }
  }, [onboarding?.login]);

  useEffect(() => {
    if (!retryAfter) return;

    const interval = setInterval(() => {
      dispatch((dispatch, getState) => {
        const current = getState().auth.retryAfter;

        if (current <= 1) {
          dispatch(clearRetryState());
        } else {
          dispatch(setRetryAfter(current - 1));
        }
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [retryAfter, dispatch]);

  // useEffect(() => {
  //   if (retryAfter > 0 && !hasRedirectedToReset) {
  //     navigate(routes.RESET);
  //     dispatch(toggleOnboard({ login: false }));
  //     dispatch(setRedirectedToReset(true));
  //   }
  // }, [retryAfter, hasRedirectedToReset, dispatch, navigate]);

  // Swipe handlers
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    startY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isMobile || !isDragging) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;

    // Only allow dragging down
    if (diff > 0) {
      setDragY(diff);
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;
    setIsDragging(false);

    // If dragged more than 150px, close the modal
    if (dragY > 150) {
      dispatch(toggleOnboard());
    }
    setDragY(0);
  };

  const initialValues = {
    email: "",
    password: "",
  };

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Required"),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password is too short - should be 8 chars minimum"),
  });

  const handleSubmit = (values) => {
    if (isSubmittingRef.current || retryAfter > 0) return;

    isSubmittingRef.current = true;
    trackLoginFormSubmit();

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.REACT_APP_GOOGLE_RE_CAPTCHA_API_KEY, {
          action: "login",
        })
        .then((token) => {
          const payload = { ...values, recaptcha_token: token };

          dispatch(toggleLoader({ isLoading: true }));

          dispatch(login(payload))
            .unwrap()
            .then((response) => {
              setMessage((prev) => ({
                ...prev,
                error: response?.error?.message || "",
              }));

              if (response?.user) {
                dispatch(toggleOnboard({ login: false }));

                const from =
                  location.state?.from?.pathname ||
                  location.pathname ||
                  routes.HOME;

                navigate(from, { replace: true });

                setMessage((prev) => ({
                  ...prev,
                  success: "Login successful!",
                }));

                trackLoginSuccess(response?.user?.id);
                dispatch(toggleReFetch("mine"));

                if (localStorage.getItem("pendingDeposit") == "true") {
                  dispatch(togglePayment({ payment: true }));
                  localStorage.removeItem("pendingDeposit");
                }
              }
            })
            .catch((error) => {
              let errorMessage = "Login failed";
              const apiError = error?.error || error;

              if (typeof apiError?.message === "string") {
                errorMessage = apiError.message;
              }

              if (typeof apiError?.retry_after === "number") {
                dispatch(setRetryAfter(apiError.retry_after));
                dispatch(setRedirectedToReset(false));
              }

              setMessage((prev) => ({
                ...prev,
                error: errorMessage,
              }));
            })
            .finally(() => {
              dispatch(toggleLoader({ isLoading: false }));
              isSubmittingRef.current = false;
            });
        })
        .catch((err) => {
          console.error("reCAPTCHA execution failed:", err);
          setMessage({ error: "Failed to verify reCAPTCHA" });

          isSubmittingRef.current = false;
        });
    });
  };

  const handleForgetPasswordModal = () => {
    setModalShow(true);
    dispatch(toggleOnboard());
  };

  const externalLogin = (link) => {
    setLoading(true);
    const currentOrigin = window.location.origin;
    console.log("currentOrigin", currentOrigin);

    if (currentOrigin === process.env.REACT_APP_FRONTEND_STAGING_URL) {
      window.open(
        `${process.env.REACT_APP_STAGING_URL}/auth/${link}/redirect`,
        "_self",
      );
    } else if (
      currentOrigin === process.env.REACT_APP_FRONTEND_PRODUCTION_URL
    ) {
      window.open(
        `${process.env.REACT_APP_PRODUCTION_URL}/auth/${link}/redirect`,
        "_self",
      );
    } else if (currentOrigin === process.env.REACT_APP_FRONTEND_LOCAL_URL) {
      window.open(
        `${process.env.REACT_APP_STAGING_URL}/auth/${link}/redirect`,
        "_self",
      );
    }
  };

  // Calculate opacity based on drag
  const backdropOpacity = isMobile ? Math.max(0, 1 - dragY / 300) : 1;

  return (
    <>
      <Toaster message={message} />
      <Modal
        size="md"
        centered={!isMobile}
        show={onboarding?.login}
        dialogClassName="modern-modal-dialog my-3"
        className={`modern-auth-modal ${isMobile ? "mobile-bottom-sheet" : ""}`}
        onHide={() => {
          trackLoginFormExit();
          dispatch(toggleOnboard());
        }}
        backdrop={true}
        animation={false}
        style={isMobile ? { "--backdrop-opacity": backdropOpacity } : {}}
      >
        <Modal.Body className="modern-modal-body m-0 p-0">
          <div
            ref={cardRef}
            className={`modern-auth-card ${
              isDarkMode ? "dark-mode" : "light-mode"
            }`}
            style={
              isMobile
                ? {
                    transform: `translateY(${dragY}px)`,
                    transition: isDragging ? "none" : "transform 0.3s ease-out",
                  }
                : {}
            }
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Swipe Handle - Only on mobile */}
            {isMobile && (
              <div className="swipe-handle-container">
                <div className="swipe-handle" />
              </div>
            )}
            <div className="d-center">
              <img
                height={104}
                alt="Droppie Logo"
                src={isDarkMode && WebLogoDark}
              />
            </div>

            <div className="auth-buttons-container">
              <button
                type="submit"
                disabled={isLoading}
                className="modern-login-btn-google"
                onClick={() => {
                  trackLoginWithGoogle();
                  externalLogin("google");
                }}
              >
                <span className="d-center">
                  <img
                    height={24}
                    width={24.49}
                    alt="Google logo"
                    src={authImages.googleColored}
                  />
                  <p className="ms-2 mb-0 text-16px text-fw-400">
                    Login With Google
                  </p>
                </span>
              </button>

              <button
                type="submit"
                disabled={isLoading}
                onClick={() => {
                  trackLoginWithFacebook();
                  externalLogin("facebook");
                }}
                className="modern-login-btn-facebook"
              >
                <span className="d-center">
                  <img
                    height={24}
                    width={24.49}
                    alt="facebook logo"
                    src={authImages.facebookBlue}
                  />
                  <p className="ms-2 mb-0">Login With Facebook</p>
                </span>
              </button>
            </div>

            {/* Divider */}
            <div className="modern-divider">
              <div className="modern-divider-line text-purple-taupe"></div>
              <span
                className={`modern-divider-text ${
                  isDarkMode ? "text-dark-charcoal-gray" : "text-silver-gray"
                }`}
              >
                OR
              </span>
              <div className="modern-divider-line text-purple-taupe"></div>
            </div>

            <Formik
              initialValues={initialValues}
              validationSchema={LoginSchema}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, handleChange, handleSubmit }) => (
                <Form>
                  {/* Email Input */}
                  <div className="modern-form-group">
                    <label className="modern-label">Email</label>
                    <div className="modern-input-wrapper">
                      <Mail className="modern-input-icon" />
                      <input
                        type="email"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        className="modern-input"
                        placeholder="e.g. john.smith@example.com"
                      />
                    </div>
                    {errors.email && touched.email && (
                      <span className="error">{errors.email}</span>
                    )}
                  </div>

                  {/* Password Input */}
                  <div className="modern-form-group">
                    <label className="modern-label">Password</label>
                    <div className="modern-input-wrapper">
                      <Lock className="modern-input-icon" />
                      <input
                        name="password"
                        placeholder="••••••••"
                        onChange={handleChange}
                        value={values.password}
                        type={showPassword ? "text" : "password"}
                        className="modern-input modern-input-password"
                      />
                      <span
                        className="modern-eye-icon"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </span>
                    </div>
                    {errors.password && touched.password && (
                      <span className="error">{errors.password}</span>
                    )}
                  </div>

                  {/* Forgot Password */}
                  <div className="d-end mt-3 mb-3">
                    <span
                      className="text-periwinkle-blue text-14px text-fw-400 cursor-pointer text-underline-hover"
                      onClick={(e) => {
                        e.preventDefault();
                        handleForgetPasswordModal();
                      }}
                    >
                      Forgot Password
                    </span>
                  </div>

                  {/* Login Button */}
                  <button
                    type="submit"
                    disabled={isLoading || retryAfter > 0}
                    className={`d-center ${
                      isDarkMode
                        ? "filled-medium-deep-yellow-btn-dark"
                        : "filled-medium-deep-purple-btn-light"
                    } ${retryAfter > 0 ? "cursor-disabled" : ""}`}
                  >
                    {retryAfter > 0 ? (
                      `Try again in ${retryAfter}s`
                    ) : isLoading ? (
                      <Loading />
                    ) : (
                      "Login"
                    )}
                  </button>
                </Form>
              )}
            </Formik>

            {/* Sign Up Button */}
            <div className="d-center mt-4 text-14px text-fw-400">
              <span
                className={`${
                  isDarkMode ? "text-medium-gray" : "text-charcoal-gray"
                }`}
              >
                Don't have an account?{" "}
              </span>{" "}
              <span
                onClick={() => {
                  trackSignupClick();
                  dispatch(toggleOnboard({ register: true }));
                }}
                className={`ms-1 ${
                  isDarkMode ? "text-periwinkle-blue" : "text-deep-purple"
                } cursor-pointer text-underline-hover`}
              >
                Sign Up
              </span>
            </div>
            {isMobile && (
              <div className="swipe-handle-container-bottom">
                <div className="swipe-handle-bottom" />
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
      <BackdropLoader show={loading} />
      <ForgetPassword
        show={modalShow}
        setLoading={setLoading}
        onHide={() => setModalShow(false)}
      />
    </>
  );
};

export default Login;
